import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { AltchaWidget } from "@/components/AltchaWidget";
import { API_URL } from "@/lib/env";

type SupportMessage = {
  id: string;
  sender: "visitor" | "support";
  body: string;
  created_at: string;
  sender_name?: string;
  sender_avatar_url?: string;
};

type StoredConversation = { id: string; token: string };

const CONVERSATION_STORAGE_KEY = "carmen-support-conversation-v1";

const TEAM_PORTRAITS = [
  { src: "/support/stijn.jpeg", name: "Stijn" },
  { src: "/support/jonas.png", name: "Jonas" },
  { src: "/support/team.svg", name: "Het Carmen-team" },
];

function SupportTeamAvatars() {
  return (
    <div role="img" aria-label="Stijn, Jonas en het Carmen-team" className="flex shrink-0 -space-x-2">
      {TEAM_PORTRAITS.map(({ src, name }, index) => (
        <img key={src} style={{ zIndex: TEAM_PORTRAITS.length - index }} src={src} alt="" title={name} width={34} height={34} className="relative h-[34px] w-[34px] rounded-full border-2 border-white bg-[#e8eef5] object-cover" />
      ))}
    </div>
  );
}

function loadStoredConversation(): StoredConversation | null {
  try {
    const raw = window.localStorage.getItem(CONVERSATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConversation>;
    return typeof parsed.id === "string" && typeof parsed.token === "string"
      ? { id: parsed.id, token: parsed.token }
      : null;
  } catch {
    return null;
  }
}

function storeConversation(conversation: StoredConversation) {
  try {
    window.localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(conversation));
  } catch {
    // The active page session still keeps the conversation available.
  }
}

function clearStoredConversation() {
  try {
    window.localStorage.removeItem(CONVERSATION_STORAGE_KEY);
  } catch {
    // Nothing else to clear.
  }
}

async function responseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = "Versturen is niet gelukt. Probeer het opnieuw.";
    try {
      const body = (await response.json()) as { detail?: string };
      if (body.detail) message = body.detail;
    } catch {
      // Keep the safe generic message.
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

function ReplyAvatar({ message }: { message: SupportMessage }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const photo = message.sender_avatar_url && message.sender_avatar_url !== failedUrl;
  return <img
    src={photo ? message.sender_avatar_url : "/carmen-icon.png"}
    alt={message.sender_name || "Carmen"}
    title={message.sender_name || "Carmen"}
    referrerPolicy="no-referrer"
    onError={() => setFailedUrl(message.sender_avatar_url || null)}
    className={`h-7 w-7 shrink-0 ${photo ? "rounded-full object-cover" : "object-contain"}`}
  />;
}

export function SupportChat() {
  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState<StoredConversation | null>(() =>
    typeof window === "undefined" ? null : loadStoredConversation(),
  );
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [altcha, setAltcha] = useState<string | null>(null);
  const [altchaAttempt, setAltchaAttempt] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const messagesEnd = useRef<HTMLDivElement>(null);
  const launcher = useRef<HTMLButtonElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);

  function closeChat() {
    setOpen(false);
    launcher.current?.focus();
  }

  useEffect(() => {
    const input = composer.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight, 112)}px`;
  }, [draft, open]);

  useEffect(() => {
    if (!open) return;
    messagesEnd.current?.scrollIntoView({ block: "nearest" });
  }, [messages, open]);

  const loadMessages = useCallback(async () => {
    if (!conversation) return;
    const response = await fetch(
      `${API_URL}/api/support/conversations/${conversation.id}/messages`,
      { headers: { "X-Support-Token": conversation.token } },
    );
    if (response.status === 403 || response.status === 404 || response.status === 410) {
      clearStoredConversation();
      setConversation(null);
      setMessages([]);
      setError("");
      setAltcha(null);
      setAltchaAttempt((current) => current + 1);
      return;
    }
    const body = await responseJson<{ messages: SupportMessage[] }>(response);
    setMessages(body.messages);
  }, [conversation]);

  useEffect(() => {
    if (!open || !conversation) return undefined;
    void loadMessages().catch(() => undefined);
    const timer = window.setInterval(() => {
      void loadMessages().catch(() => undefined);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [conversation, loadMessages, open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const message = draft.trim();
    if (!message || sending) return;
    setSending(true);
    setError("");
    try {
      if (!conversation) {
        if (!altcha) throw new Error("Een momentje, de veilige verbinding wordt voorbereid.");
        const response = await fetch(`${API_URL}/api/support/conversations`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message, source: "website", altcha }),
        });
        const created = await responseJson<{
          id: string;
          token: string;
          messages: SupportMessage[];
        }>(response);
        const stored = { id: created.id, token: created.token };
        storeConversation(stored);
        setConversation(stored);
        setMessages(created.messages);
      } else {
        const response = await fetch(
          `${API_URL}/api/support/conversations/${conversation.id}/messages`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Support-Token": conversation.token,
            },
            body: JSON.stringify({ message }),
          },
        );
        if ([403, 404, 410].includes(response.status)) {
          clearStoredConversation();
          setConversation(null);
          setMessages([]);
          throw new Error("Dit gesprek is afgesloten. Verstuur je bericht opnieuw om een nieuwe chat te starten.");
        }
        const body = await responseJson<{ message: SupportMessage }>(response);
        setMessages((current) => [...current, body.message]);
      }
      setDraft("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Versturen is niet gelukt.");
      if (!conversation) {
        setAltcha(null);
        setAltchaAttempt((current) => current + 1);
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <aside
      className="fixed bottom-4 right-3 z-[80] flex max-w-[calc(100vw-1.5rem)] flex-col items-end gap-4 sm:bottom-5 sm:right-5"
      onKeyDown={(event) => {
        if (event.key === "Escape" && open) {
          event.stopPropagation();
          closeChat();
        }
      }}
    >
      {open ? (
        <section
          id="carmen-support-panel"
          aria-labelledby="carmen-support-title"
          className="flex h-[min(616px,calc(100dvh-104px))] w-[400px] max-w-full flex-col overflow-hidden rounded-[24px] border border-[#e5e9ee] bg-white shadow-[0_12px_48px_rgba(16,35,55,0.18)]"
        >
          <header className="flex shrink-0 items-center gap-2.5 border-b border-[#edf0f3] px-4 py-3">
            <SupportTeamAvatars />
            <div className="min-w-0 flex-1">
              <h2 id="carmen-support-title" className="text-[14px] font-semibold leading-5 tracking-[-0.01em] text-[#173b60]">Carmen support</h2>
              <p className="text-[11px] leading-4 text-[#78828e]">Stijn, Jonas of het team</p>
            </div>
            <button
              type="button"
              onClick={closeChat}
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-[#78828e] transition-colors hover:bg-[#f1f4f7] hover:text-[#173b60] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0a63c7]"
              aria-label="Chat sluiten"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-4 w-4" strokeWidth="1.8" aria-hidden="true">
                <path strokeLinecap="round" d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
            <p className="mx-auto mb-6 max-w-[290px] text-center text-[13px] leading-[21px] text-[#78828e]">Echte mensen, we antwoorden persoonlijk.</p>
            <div role="log" aria-label="Chatberichten" aria-live="polite" aria-relevant="additions text" className="space-y-3">
              {messages.map((message) =>
                message.sender === "visitor" ? (
                  <div
                    key={message.id}
                    className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-[18px] rounded-br-[5px] bg-[#0b4d82] px-4 py-3 text-[14px] leading-[21px] text-white [overflow-wrap:anywhere]"
                  >
                    {message.body}
                  </div>
                ) : (
                  <div key={message.id} className="flex items-end gap-2">
                    <ReplyAvatar message={message} />
                    <div className="max-w-[85%] whitespace-pre-wrap rounded-[18px] rounded-bl-[5px] bg-[#f0f3f6] px-4 py-3 text-[14px] leading-[21px] text-[#243d55] [overflow-wrap:anywhere]">
                      {message.body}
                    </div>
                  </div>
                ),
              )}
            </div>
            <div ref={messagesEnd} />
          </div>

          <form onSubmit={submit} className="relative shrink-0 bg-white px-4 pb-4 pt-2">
            {!conversation ? (
              <div aria-hidden="true" className="hidden">
                <AltchaWidget
                  key={altchaAttempt}
                  challengeUrl={`${API_URL}/api/auth/otp/challenge`}
                  onPayload={setAltcha}
                />
              </div>
            ) : null}
            <div className="rounded-[18px] border border-[#e5e9ee] bg-white px-3.5 pb-2 pt-3 shadow-[0_2px_6px_rgba(16,35,55,0.02)] transition-colors focus-within:border-[#79afe8]">
              <label className="block">
                <span className="sr-only">Bericht</span>
                <textarea
                  ref={composer}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                      event.preventDefault();
                      event.currentTarget.form?.requestSubmit();
                    }
                  }}
                  placeholder="Typ je bericht…"
                  rows={1}
                  maxLength={2000}
                  className="block max-h-28 min-h-6 w-full resize-none bg-transparent text-[14px] leading-6 text-[#173b60] outline-none placeholder:text-[#8b939d]"
                />
              </label>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="hidden text-[10px] text-[#929aa4] sm:inline">Shift + Enter voor een nieuwe regel</span>
                <button
                  type="submit"
                  disabled={sending || !draft.trim()}
                  className="ml-auto flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#0b4d82] text-white transition-colors hover:bg-[#093b64] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0a63c7] disabled:cursor-not-allowed disabled:bg-[#f0f2f4] disabled:text-[#b9c0c8]"
                  aria-label={sending ? "Bericht wordt verstuurd" : "Bericht versturen"}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-4 w-4" strokeWidth="1.8" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m-5 5 5-5 5 5" />
                  </svg>
                </button>
              </div>
            </div>
            {error ? <p role="alert" className="mt-2 text-[12px] text-red-600">{error}</p> : null}
          </form>
        </section>
      ) : null}

      <button
        ref={launcher}
        type="button"
        onClick={() => {
          setOpen((current) => !current);
        }}
        className="group relative flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#0a63c7]"
        aria-label={open ? "Chat minimaliseren" : "Chat met iemand van Carmen"}
        aria-expanded={open}
        aria-controls={open ? "carmen-support-panel" : undefined}
      >
        {!open ? (
          <span aria-hidden="true" className="pointer-events-none invisible absolute right-full top-1/2 mr-3 w-max max-w-[calc(100vw-6rem)] -translate-y-1/2 rounded-full border border-[#e5e9ee] bg-white px-4 py-2.5 text-[12px] font-medium text-[#173b60] opacity-0 shadow-[0_4px_20px_rgba(16,35,55,0.08)] transition-opacity group-hover:visible group-hover:opacity-100 group-focus-visible:visible group-focus-visible:opacity-100 motion-reduce:transition-none">Chat met iemand van Carmen</span>
        ) : null}
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full shadow-[0_6px_24px_rgba(10,52,96,0.2)] transition-transform group-hover:-translate-y-0.5 motion-reduce:transform-none ${open ? "bg-[#0b4d82] text-white" : "border border-[#e5e9ee] bg-white"}`}>
          {open ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5" strokeWidth="1.8" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
            </svg>
          ) : (
            <img src="/carmen-icon.png" alt="" className="h-8 w-8 object-contain" />
          )}
        </span>
      </button>
    </aside>
  );
}
